import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton as MUIIconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Avatar,
  InputAdornment,
} from '@mui/material';
import { Add, Edit, Delete, LocalShipping, Phone, Email, LocationOn, Schedule, Visibility, VisibilityOff, Assessment, Close, TrendingUp, CheckCircle, AccessTime, Cancel } from '@mui/icons-material';
import { collection, getDocs, deleteDoc, doc, addDoc, updateDoc, getDoc, onSnapshot } from '../../services/firestoreCompat';
import { db, auth } from '../../services/firebaseConfig';
import { supabase } from '../../services/supabaseClient';

interface Driver {
  id: string | number;
  name: string;
  map_code: string;  // 1–4 char label shown on the POS map marker (e.g. "F01")
  email: string;
  phone: string;
  vehicle_type: 'bike' | 'scooter' | 'car';
  license_plate: string;
  status: 'available' | 'busy' | 'offline';
  current_location: string;
  current_latitude?: number;
  current_longitude?: number;
  deliveries_today: number;
  rating: number;
  joined_date: string;
}

const DriversPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingDriver, setEditingDriver] = useState<Driver | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  // Driver Report Dialog
  const [reportDriver, setReportDriver] = useState<Driver | null>(null);
  const [reportData, setReportData] = useState<{
    deliveries: any[];
    totalDeliveries: number;
    totalEarnings: number;
    todayDeliveries: number;
    weekDeliveries: number;
    monthDeliveries: number;
    activeHistory: Array<{ date: string; sessions: number; hours: number }>;
    avgDeliveryTime: number;
  } | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    map_code: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    vehicle_type: 'bike' as 'bike' | 'scooter' | 'car',
    license_plate: '',
    current_location: '',
  });

  // Real-time Firestore listeners — no polling, instant updates
  useEffect(() => {
    setLoading(true);

    // Listen to drivers collection in real-time
    const unsubDrivers = onSnapshot(collection(db, 'drivers'), (snap) => {
      const driversData: Driver[] = [];
      snap.forEach((d) => {
        const data = d.data();
        driversData.push({
          id: d.id,
          // Supabase column is `full_name`; legacy Firestore docs used `name`.
          // Read both so the page works against either source.
          name: data.full_name || data.name || 'Unknown',
          map_code: data.map_code || '',
          email: data.email || '',
          phone: data.phone || '',
          vehicle_type: data.vehicle_type || 'bike',
          // Supabase column is `vehicle_plate`; legacy was `license_plate`.
          license_plate: data.vehicle_plate || data.license_plate || '',
          status: (data.status || (data.is_available ? 'available' : 'offline')) as 'available' | 'busy' | 'offline',
          current_location: data.current_location || '',
          current_latitude: data.current_latitude || data.latitude,
          current_longitude: data.current_longitude || data.longitude,
          deliveries_today: data.deliveries_today || 0,
          rating: data.rating || 5.0,
          joined_date: (() => {
            // Handle Firestore Timestamp, Date, or string
            const raw = data.joined_date || data.created_at;
            if (!raw) return new Date().toISOString().split('T')[0];
            try {
              if (typeof raw === 'string') {
                const parsed = new Date(raw);
                if (!isNaN(parsed.getTime())) return parsed.toISOString().split('T')[0];
                return raw;
              }
              if (raw.toDate && typeof raw.toDate === 'function') return raw.toDate().toISOString().split('T')[0];
              if (raw.seconds) return new Date(raw.seconds * 1000).toISOString().split('T')[0];
              if (raw instanceof Date) return raw.toISOString().split('T')[0];
            } catch { /* fall through */ }
            return new Date().toISOString().split('T')[0];
          })(),
        });
      });
      driversData.sort((a, b) => a.name.localeCompare(b.name));
      setDrivers(driversData);
      setLoading(false);
    }, (err) => {
      console.error('Driver listener error:', err);
      setError('Failed to load drivers.');
      setLoading(false);
    });

    // Listen to driver_locations for real-time GPS updates
    const unsubLocations = onSnapshot(collection(db, 'driver_locations'), (snap) => {
      setDrivers(prev => {
        const updated = [...prev];
        snap.forEach((d) => {
          const loc = d.data();
          const idx = updated.findIndex(dr => dr.email === loc.driver_email || String(dr.id) === loc.driver_id);
          if (idx >= 0) {
            if (loc.latitude && loc.longitude) {
              updated[idx] = {
                ...updated[idx],
                current_latitude: loc.latitude,
                current_longitude: loc.longitude,
                current_location: `${loc.latitude.toFixed(4)}, ${loc.longitude.toFixed(4)}`,
                status: loc.is_online ? 'available' : 'offline',
              };
            }
          }
        });
        return updated;
      });
    });

    return () => { unsubDrivers(); unsubLocations(); };
  }, []);

  // Open driver report dialog — loads delivery history + active sessions
  const handleOpenReport = async (driver: Driver) => {
    setReportDriver(driver);
    setReportLoading(true);
    setReportData(null);
    try {
      const { collection, query, where, getDocs, limit } = await import('../../services/firestoreCompat');

      // Load all delivery orders assigned to this driver
      // No orderBy — avoids composite index requirement; sort client-side
      const ordersRef = collection(db, 'orders');
      const ordersSnap = await getDocs(query(
        ordersRef,
        where('rider_email', '==', driver.email),
        limit(500)
      ));

      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const weekStart = new Date(todayStart); weekStart.setDate(weekStart.getDate() - 7);
      const monthStart = new Date(todayStart); monthStart.setDate(monthStart.getDate() - 30);

      const deliveries: any[] = [];
      let totalEarnings = 0, todayCount = 0, weekCount = 0, monthCount = 0;
      let totalDuration = 0, durationCount = 0;

      ordersSnap.forEach((d) => {
        const data = d.data();
        const createdAt = data.created_at?.toDate?.() || new Date(data.created_at);
        const deliveredAt = data.delivered_at?.toDate?.() || (data.status === 'delivered' || data.status === 'completed' ? data.updated_at?.toDate?.() : null);
        const isCompleted = data.status === 'delivered' || data.status === 'completed';

        if (isCompleted) {
          totalEarnings += parseFloat(data.total || '0');
          if (createdAt >= todayStart) todayCount++;
          if (createdAt >= weekStart) weekCount++;
          if (createdAt >= monthStart) monthCount++;
          if (deliveredAt && createdAt) {
            const duration = (deliveredAt.getTime() - createdAt.getTime()) / (1000 * 60);
            if (duration > 0 && duration < 180) { // Ignore unreasonable durations
              totalDuration += duration;
              durationCount++;
            }
          }
        }

        deliveries.push({
          id: d.id,
          order_number: data.order_number || d.id.substring(0, 8),
          customer_name: data.customer_name || '',
          total: data.total || 0,
          status: data.status || 'pending',
          created_at: createdAt,
          delivered_at: deliveredAt,
          address: data.delivery_address || data.customer_address || '',
        });
      });

      // Load driver location history for active sessions (last 30 days)
      // driver_locations has a single doc per driver — for session history we'd need a driver_sessions collection
      // For now, estimate sessions from delivery patterns
      const deliveryByDay: Record<string, { count: number; firstTime: Date; lastTime: Date }> = {};
      deliveries.forEach(d => {
        if (d.status !== 'delivered' && d.status !== 'completed') return;
        const day = d.created_at.toISOString().split('T')[0];
        if (!deliveryByDay[day]) {
          deliveryByDay[day] = { count: 0, firstTime: d.created_at, lastTime: d.created_at };
        }
        deliveryByDay[day].count++;
        if (d.created_at < deliveryByDay[day].firstTime) deliveryByDay[day].firstTime = d.created_at;
        if (d.created_at > deliveryByDay[day].lastTime) deliveryByDay[day].lastTime = d.created_at;
      });

      const activeHistory = Object.entries(deliveryByDay)
        .map(([date, info]) => ({
          date,
          sessions: 1, // Approximation — one session per day
          hours: Math.max(0.5, (info.lastTime.getTime() - info.firstTime.getTime()) / (1000 * 60 * 60)),
        }))
        .sort((a, b) => b.date.localeCompare(a.date))
        .slice(0, 30);

      const completedCount = deliveries.filter(d => d.status === 'delivered' || d.status === 'completed').length;

      setReportData({
        // Sort by date desc (newest first) — done client-side since we don't use orderBy
        deliveries: deliveries.sort((a, b) => b.created_at.getTime() - a.created_at.getTime()).slice(0, 50),
        totalDeliveries: completedCount,
        totalEarnings: Math.round(totalEarnings * 100) / 100,
        todayDeliveries: todayCount,
        weekDeliveries: weekCount,
        monthDeliveries: monthCount,
        activeHistory,
        avgDeliveryTime: durationCount > 0 ? Math.round(totalDuration / durationCount) : 0,
      });
    } catch (err: any) {
      console.error('Report load error:', err);
      setError('Failed to load driver report: ' + err.message);
    } finally {
      setReportLoading(false);
    }
  };

  const handleOpenDialog = (driver?: Driver) => {
    if (driver) {
      setEditingDriver(driver);
      setFormData({
        name: driver.name,
        map_code: driver.map_code || '',
        email: driver.email,
        password: '', // Empty when editing
        confirmPassword: '',
        phone: driver.phone,
        vehicle_type: driver.vehicle_type,
        license_plate: driver.license_plate,
        current_location: driver.current_location,
      });
    } else {
      setEditingDriver(null);
      setFormData({
        name: '',
        map_code: '',
        email: '',
        password: '',
        confirmPassword: '',
        phone: '',
        vehicle_type: 'bike',
        license_plate: '',
        current_location: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingDriver(null);
    setFormData({
      name: '',
      map_code: '',
      email: '',
      password: '',
      confirmPassword: '',
      phone: '',
      vehicle_type: 'bike',
      license_plate: '',
      current_location: '',
    });
  };

  const handleSaveDriver = async () => {
    if (editingDriver) {
      try {
        console.log('🔥 Updating driver in Firebase...');

        // Warn if email is changing — Firebase Auth email is NOT auto-updated
        if (editingDriver.email !== formData.email) {
          const ok = window.confirm(
            `You are changing the driver's email from:\n  ${editingDriver.email}\nto:\n  ${formData.email}\n\n` +
            `⚠ The driver's LOGIN email in Firebase Auth will NOT change automatically. ` +
            `They will still log in with the OLD email unless you also set a new password (which recreates the Auth user with the new email).\n\n` +
            `Continue with the Firestore update?`
          );
          if (!ok) return;
        }
        console.log('📋 [DEBUG] Form data password field:', formData.password ? `"${formData.password}" (length: ${formData.password.length})` : 'EMPTY');
        console.log('📋 [DEBUG] Form data confirmPassword field:', formData.confirmPassword ? `"${formData.confirmPassword}" (length: ${formData.confirmPassword.length})` : 'EMPTY');
        
        // Update password if provided — uses the `update-driver-password`
        // Supabase Edge Function (server-side service-role; the client can't
        // change another user's password). The old Firebase Cloud Function
        // fallback chain was removed — Saheeh has no Firebase project.
        if (formData.password && formData.password.length > 0) {
          if (formData.password !== formData.confirmPassword) {
            alert('Passwords do not match!');
            return;
          }
          if (formData.password.length < 6) {
            alert('Password must be at least 6 characters!');
            return;
          }
          console.log('🔐 [UPDATE] Resetting driver password via edge function...');
          const { data: pwResult, error: pwErr } = await supabase.functions.invoke('update-driver-password', {
            body: {
              driver_id:    editingDriver.id,
              new_password: formData.password,
            },
          });
          if (pwErr || (pwResult && pwResult.error)) {
            let msg = pwResult?.error || pwErr?.message || 'Password update failed';
            try {
              const ctx: any = (pwErr as any)?.context;
              if (ctx && typeof ctx.json === 'function') {
                const body = await ctx.json();
                if (body?.error) msg = body.error;
              }
            } catch { /* keep what we have */ }
            alert(`Failed to update password: ${msg}`);
            return;
          }
          console.log('✅ [UPDATE] Password reset — rider can log in with new password.');
        }
        
        // Update driver document in Firestore
        console.log('📋 [UPDATE] Driver ID:', editingDriver.id);
        console.log('📋 [UPDATE] Update data:', formData);
        console.log('📋 [DEBUG] Password in formData:', formData.password ? `YES (length: ${formData.password.length})` : 'NO');
        
        const driverRef = doc(db, 'drivers', editingDriver.id.toString());
        
        // Normalize map_code: trim, uppercase, clamp to 4 chars
        const normalizedMapCode = (formData.map_code || '').trim().toUpperCase().substring(0, 4);

        // Supabase column names (drivers table):
        //   full_name      ← form 'name'
        //   vehicle_plate  ← form 'license_plate'
        //   map_code, email, phone, vehicle_type are 1:1.
        // current_location isn't a column; real GPS lives in driver_locations.
        const updateData: any = {
          full_name:     formData.name,
          map_code:      normalizedMapCode,
          email:         formData.email,
          phone:         formData.phone,
          vehicle_type:  formData.vehicle_type,
          vehicle_plate: formData.license_plate,
          updated_at:    new Date().toISOString(),
        };
        
        console.log('📋 [UPDATE] Sending to Firestore:', updateData);
        await updateDoc(driverRef, updateData);
        console.log('✅ Driver updated in Firestore');

        // PROPAGATE NAME/PHONE CHANGES TO ACTIVE ORDERS + SESSIONS
        // Orders store a snapshot of rider_name when assigned — we need to update active ones
        try {
          const nameChanged = editingDriver.name !== formData.name;
          const phoneChanged = editingDriver.phone !== formData.phone;
          const emailChanged = editingDriver.email !== formData.email;

          if (nameChanged || phoneChanged || emailChanged) {
            console.log('🔄 [PROPAGATE] Name/phone changed — updating active orders...');
            const { collection: col, query: q, where: w, getDocs: gd, writeBatch } = await import('../../services/firestoreCompat');
            const ordersRef = col(db, 'orders');

            // Find all active (non-completed/cancelled) orders assigned to this driver
            const ordersSnap = await gd(q(
              ordersRef,
              w('rider_email', '==', editingDriver.email)
            ));

            const batch = writeBatch(db);
            let updated = 0;
            // Positive allow-list — only propagate driver info to orders that
            // are genuinely in the delivery pipeline. Prevents accidentally
            // rewriting driver info on orders stuck in ghost statuses.
            const DELIVERY_ACTIVE_STATUSES = ['pending', 'confirmed', 'preparing', 'ready', 'assigned', 'picking_up', 'in_transit'];
            ordersSnap.forEach((od) => {
              const odata = od.data();
              const status = (odata.status || '').toLowerCase();
              if (DELIVERY_ACTIVE_STATUSES.includes(status)) {
                const patch: any = { updated_at: new Date().toISOString() };
                if (nameChanged) patch.rider_name = formData.name;
                if (phoneChanged) patch.rider_phone = formData.phone;
                if (emailChanged) patch.rider_email = formData.email;
                batch.update(od.ref, patch);
                updated++;
              }
            });
            if (updated > 0) {
              await batch.commit();
              console.log(`✅ [PROPAGATE] Updated ${updated} active orders with new driver info`);
            } else {
              console.log('ℹ️ [PROPAGATE] No active orders to update');
            }

            // Also update driver_locations doc name (used by POS/Admin map)
            try {
              const { doc: dfn } = await import('../../services/firestoreCompat');
              const locRef = dfn(db, 'driver_locations', editingDriver.id.toString());
              const locPatch: any = {
                driver_name: formData.name,
                driver_phone: formData.phone,
                updated_at: new Date().toISOString(),
              };
              if (emailChanged) locPatch.driver_email = formData.email;
              await updateDoc(locRef, locPatch).catch(() => { /* doc may not exist yet */ });
            } catch { /* ignore */ }

            // Update admin_users and pos_users if driver is also registered there
            try {
              const { collection: c2, query: q2, where: w2, getDocs: g2, writeBatch: wb2 } = await import('../../services/firestoreCompat');
              for (const coll of ['admin_users', 'pos_users', 'users']) {
                const snap = await g2(q2(c2(db, coll), w2('email', '==', editingDriver.email)));
                if (!snap.empty) {
                  const b = wb2(db);
                  snap.forEach((d2) => {
                    const p: any = { name: formData.name, phone: formData.phone, updated_at: new Date().toISOString() };
                    if (emailChanged) p.email = formData.email;
                    b.update(d2.ref, p);
                  });
                  await b.commit();
                  console.log(`✅ [PROPAGATE] Updated ${snap.size} ${coll} record(s)`);
                }
              }
            } catch (propErr) { console.warn('[PROPAGATE] Could not update other collections:', propErr); }
          }
        } catch (propagateErr) {
          console.error('⚠️ [PROPAGATE] Order propagation failed:', propagateErr);
        }
        
        // Only show success and reload if password update completed OR no password was provided
        if (!formData.password || formData.password.length === 0) {
          console.log('ℹ️ [UPDATE] No password provided - skipping password update');
        }
        
        console.log('═══════════════════════════════════════════════════════');
        console.log('🎉 [UPDATE] ALL OPERATIONS COMPLETE!');
        console.log('═══════════════════════════════════════════════════════');
        console.log('📧 Driver Email:', editingDriver.email);
        console.log('✅ Firestore document updated');
        console.log('✅ Password update completed (if provided)');
        console.log('═══════════════════════════════════════════════════════');
        console.log('');
        console.log('💡 LOGS ABOVE SHOW COMPLETE PROCESS - NO PAGE RELOAD!');
        console.log('💡 Check console for detailed STEP 1 and STEP 2 logs');
        console.log('');
        
        // Close dialog first
        handleCloseDialog();
        
        // Refresh driver data without reloading page (preserves console logs)
        console.log('🔄 Refreshing driver list (without page reload)...');
        // onSnapshot auto-updates — no manual refresh needed
        
        // Show success message after a small delay to ensure logs are visible
        setTimeout(() => {
          alert('Driver updated successfully!\n\n✅ Check console logs for detailed password update process.');
        }, 500);
      } catch (error: any) {
        console.error('❌ Error updating driver:', error);
        alert(`Failed to update driver: ${error.message}`);
      }
    } else {
      // Create new driver via the `create-driver` Supabase Edge Function.
      // The function runs server-side with the service-role key, so it can
      // create the auth user (which is never allowed from the client) +
      // the `drivers` and `staff_users` rows in one transaction. Caller
      // must be a tenant_admin (the function verifies the JWT).
      try {
        // ── Client-side validation (so the operator gets fast, specific
        //    feedback instead of the generic "non-2xx" toast from Supabase). ──
        const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim());
        if (!emailOk) {
          alert('Please enter a valid email address (e.g. rider@saheehpos.com).');
          return;
        }
        if (!formData.password || formData.password.length < 6) {
          alert('Password must be at least 6 characters.');
          return;
        }
        if (!formData.phone) {
          alert('Phone is required.');
          return;
        }
        if (!formData.name) {
          alert('Full name is required.');
          return;
        }

        console.log('🛰️ [CREATE] Invoking create-driver edge function...');
        const { data: result, error: fnError } = await supabase.functions.invoke('create-driver', {
          body: {
            email:         formData.email.trim(),
            password:      formData.password,
            name:          formData.name.trim(),
            phone:         formData.phone.trim(),
            map_code:      formData.map_code,
            vehicle_type:  formData.vehicle_type,
            license_plate: formData.license_plate,
          },
        });

        if (fnError || (result && result.error)) {
          // supabase-js wraps non-2xx as FunctionsHttpError and stashes the
          // real Response on .context — pull the body out so the operator
          // sees the actual reason (e.g. "invalid email format") instead of
          // the generic "non-2xx status code" toast.
          let detail = result?.error || fnError?.message || 'Edge function failed';
          try {
            const ctx: any = (fnError as any)?.context;
            if (ctx && typeof ctx.json === 'function') {
              const body = await ctx.json();
              if (body?.error) detail = body.error;
            }
          } catch { /* fall through with whatever we have */ }
          throw new Error(detail);
        }

        console.log('✅ [CREATE] Driver created — uid:', result?.uid, 'driver_id:', result?.driver_id);
        handleCloseDialog();
        alert(`Driver created!\nEmail: ${formData.email}\nPassword: ${formData.password}\n\nThey can now log in to the Rider app.`);
      } catch (error: any) {
        console.error('❌ Error creating driver:', error);
        alert(`Failed to create driver: ${error.message}`);
      }
    }
  };

  const handleDeleteDriver = async (id: string | number) => {
    try {
      console.log('🗑️ Deleting driver from Firebase:', id);
      
      // Delete from Firebase Firestore
      await deleteDoc(doc(db, 'drivers', id.toString()));
      
      console.log('✅ Driver deleted successfully');
      
      // Update local state
      setDrivers(drivers.filter(driver => driver.id !== id));
    } catch (error) {
      console.error('❌ Failed to delete driver:', error);
      setError('Failed to delete driver. Please try again.');
    }
  };

  // ─── Helpers ─────────────────────────────────────────
  const statusConfig = (s: string) => {
    if (s === 'available') return { label: 'Online', color: '#4CAF50', bg: 'rgba(76,175,80,0.12)' };
    if (s === 'busy') return { label: 'Busy', color: '#FF9800', bg: 'rgba(255,152,0,0.12)' };
    return { label: 'Offline', color: '#6b7280', bg: 'rgba(107,114,128,0.12)' };
  };
  const vehicleEmoji = (v: string) => v === 'car' ? '🚗' : v === 'scooter' ? '🛵' : '🚲';
  const getInitials = (name: string) => name.split(' ').map(n => n?.[0] || '').join('').toUpperCase().slice(0, 2);
  const formatLocation = (d: Driver) => {
    if (d.current_latitude && d.current_longitude && d.current_latitude !== 0) {
      return `${d.current_latitude.toFixed(4)}, ${d.current_longitude.toFixed(4)}`;
    }
    return null;
  };

  // Stats
  const onlineCount = drivers.filter(d => d.status === 'available').length;
  const busyCount = drivers.filter(d => d.status === 'busy').length;
  const offlineCount = drivers.filter(d => d.status === 'offline').length;
  const totalDeliveries = drivers.reduce((s, d) => s + d.deliveries_today, 0);

  return (
    <AdminLayout>
      <Box sx={{ p: { xs: 1.5, md: 3 }, maxWidth: '100%' }}>
        {/* ── Header ──────────────────────────────────────────── */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
          <Box>
            <Typography sx={{ fontSize: { xs: '1.3rem', md: '1.5rem' }, fontWeight: 700, color: '#e0e0e0' }}>
              {t('drivers.title')}
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: '#6b7280' }}>
              {drivers.length} drivers · {onlineCount} online · {totalDeliveries} deliveries today
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}
            sx={{ backgroundColor: '#00C896', color: '#1a1d2e', fontWeight: 600, textTransform: 'none',
              borderRadius: 2, '&:hover': { backgroundColor: '#7bc912' } }}>
            {t('drivers.addDriver')}
          </Button>
        </Box>

        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: '#00C896' }} /></Box>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!loading && !error && (
          <>
            {/* ── KPI Cards (compact) ────────────────────────────── */}
            <Box sx={{ display: 'flex', gap: { xs: 1, md: 2 }, mb: 2.5, flexWrap: 'wrap' }}>
              {[
                { label: t('drivers.totalDrivers'), value: drivers.length, color: '#00C896', icon: <LocalShipping /> },
                { label: t('common.available'), value: onlineCount, color: '#4CAF50', icon: <Schedule /> },
                { label: t('drivers.busy'), value: busyCount, color: '#FF9800', icon: <LocationOn /> },
                { label: 'Offline', value: offlineCount, color: '#6b7280', icon: <Schedule /> },
                { label: t('drivers.todaysDeliveries'), value: totalDeliveries, color: '#2196F3', icon: <Phone /> },
              ].map((kpi, i) => (
                <Box key={i} sx={{ flex: '1 1 140px', minWidth: 120, p: 1.5, backgroundColor: '#252838', borderRadius: 2,
                  border: '1px solid #3a3d52', textAlign: 'center' }}>
                  <Box sx={{ color: kpi.color, mb: 0.3 }}>{kpi.icon}</Box>
                  <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: kpi.color }}>{kpi.value}</Typography>
                  <Typography sx={{ fontSize: '0.68rem', color: '#6b7280', fontWeight: 500 }}>{kpi.label}</Typography>
                </Box>
              ))}
            </Box>

            {/* ── Driver Table ────────────────────────────────────── */}
            <TableContainer sx={{ backgroundColor: '#252838', borderRadius: '12px', border: '1px solid #3a3d52',
              maxHeight: 'calc(100vh - 280px)', overflow: 'auto' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {[t('drivers.driver'), t('drivers.vehicle'), t('common.status'), t('drivers.location'),
                      t('drivers.deliveriesToday'), t('drivers.rating'), t('common.actions')].map((h, i) => (
                      <TableCell key={i} align={i === 6 ? 'center' : 'left'}
                        sx={{ backgroundColor: '#1a1d2e !important', color: '#e0e0e0 !important', fontWeight: 600,
                          fontSize: '0.82rem', borderBottom: '2px solid #3a3d52 !important', whiteSpace: 'nowrap' }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {drivers.map((driver, idx) => {
                    const sc = statusConfig(driver.status);
                    const loc = formatLocation(driver);
                    return (
                      <TableRow key={driver.id} sx={{
                        backgroundColor: idx % 2 === 0 ? '#252838 !important' : '#2a2d42 !important',
                        '&:hover': { backgroundColor: '#2f3350 !important' },
                      }}>
                        {/* Driver */}
                        <TableCell sx={{ borderBottom: '1px solid #3a3d52 !important' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar sx={{ width: 36, height: 36, fontSize: '0.78rem', fontWeight: 700,
                              backgroundColor: `${sc.color}25`, color: sc.color, border: `2px solid ${sc.color}40` }}>
                              {getInitials(driver.name)}
                            </Avatar>
                            <Box>
                              <Typography sx={{ color: '#e0e0e0', fontWeight: 600, fontSize: '0.88rem' }}>{driver.name}</Typography>
                              <Typography sx={{ color: '#6b7280', fontSize: '0.75rem' }}>{driver.phone || driver.email}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        {/* Vehicle */}
                        <TableCell sx={{ borderBottom: '1px solid #3a3d52 !important' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                            <Typography sx={{ fontSize: '1.1rem' }}>{vehicleEmoji(driver.vehicle_type)}</Typography>
                            <Box>
                              <Typography sx={{ color: '#e0e0e0', fontWeight: 500, fontSize: '0.85rem', textTransform: 'capitalize' }}>{driver.vehicle_type}</Typography>
                              {driver.license_plate && <Typography sx={{ color: '#6b7280', fontSize: '0.72rem' }}>{driver.license_plate}</Typography>}
                            </Box>
                          </Box>
                        </TableCell>
                        {/* Status */}
                        <TableCell sx={{ borderBottom: '1px solid #3a3d52 !important' }}>
                          <Chip label={sc.label} size="small"
                            sx={{ height: 24, fontSize: '0.72rem', fontWeight: 600,
                              backgroundColor: sc.bg, color: sc.color, border: `1px solid ${sc.color}35` }} />
                        </TableCell>
                        {/* Location */}
                        <TableCell sx={{ borderBottom: '1px solid #3a3d52 !important' }}>
                          {loc ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                              <Typography sx={{ color: '#9ca3af', fontSize: '0.8rem', fontFamily: 'monospace' }}>{loc}</Typography>
                              <Button size="small" startIcon={<LocationOn sx={{ fontSize: 14 }} />}
                                onClick={() => window.open(`https://www.google.com/maps?q=${driver.current_latitude},${driver.current_longitude}`, '_blank')}
                                sx={{ fontSize: '0.68rem', minWidth: 0, px: 1, py: 0.2, textTransform: 'none', color: '#64B5F6' }}>
                                Map
                              </Button>
                            </Box>
                          ) : (
                            <Typography sx={{ color: '#4a4d62', fontSize: '0.8rem', fontStyle: 'italic' }}>Not available</Typography>
                          )}
                        </TableCell>
                        {/* Deliveries */}
                        <TableCell sx={{ borderBottom: '1px solid #3a3d52 !important' }}>
                          <Typography sx={{ color: driver.deliveries_today > 0 ? '#00C896' : '#6b7280', fontWeight: 600, fontSize: '0.92rem' }}>
                            {driver.deliveries_today}
                          </Typography>
                        </TableCell>
                        {/* Rating */}
                        <TableCell sx={{ borderBottom: '1px solid #3a3d52 !important' }}>
                          <Typography sx={{ color: '#FF9800', fontWeight: 600, fontSize: '0.88rem' }}>
                            {driver.rating.toFixed(1)} ★
                          </Typography>
                        </TableCell>
                        {/* Actions */}
                        <TableCell align="center" sx={{ borderBottom: '1px solid #3a3d52 !important' }}>
                          <MUIIconButton size="small" onClick={() => handleOpenReport(driver)}
                            title="View Report"
                            sx={{ color: '#00C896', mr: 0.5, '&:hover': { backgroundColor: 'rgba(0, 200, 150,0.1)' } }}>
                            <Assessment fontSize="small" />
                          </MUIIconButton>
                          <MUIIconButton size="small" onClick={() => handleOpenDialog(driver)}
                            title="Edit"
                            sx={{ color: '#64B5F6', mr: 0.5, '&:hover': { backgroundColor: 'rgba(33,150,243,0.1)' } }}>
                            <Edit fontSize="small" />
                          </MUIIconButton>
                          <MUIIconButton size="small" onClick={() => handleDeleteDriver(driver.id)}
                            title="Delete"
                            sx={{ color: '#ef5350', '&:hover': { backgroundColor: 'rgba(244,67,54,0.1)' } }}>
                            <Delete fontSize="small" />
                          </MUIIconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Add/Edit Driver Dialog — clean dark theme */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="sm" fullWidth
          PaperProps={{ sx: { backgroundColor: '#252838', borderRadius: 3, border: '1px solid #3a3d52' } }}
        >
          <DialogTitle sx={{ color: '#e0e0e0', fontWeight: 700, fontSize: '1.1rem', borderBottom: '1px solid #3a3d52', pb: 1.5 }}>
            {editingDriver ? t('drivers.editDriver') : t('drivers.addDriver')}
          </DialogTitle>
          <DialogContent sx={{ pt: '24px !important', px: { xs: 2, md: 3 } }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Name & Email row */}
              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                <TextField fullWidth label={t('drivers.fullName')} value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  variant="outlined" InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#1e2139', color: '#e0e0e0', '& fieldset': { borderColor: '#3a3d52' }, '&:hover fieldset': { borderColor: '#00C896' } },
                    '& .MuiInputLabel-root': { color: '#9ca3af', '&.Mui-focused': { color: '#00C896' } } }} />
                <TextField fullWidth label={t('common.email')} value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  variant="outlined" InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#1e2139', color: '#e0e0e0', '& fieldset': { borderColor: '#3a3d52' }, '&:hover fieldset': { borderColor: '#00C896' } },
                    '& .MuiInputLabel-root': { color: '#9ca3af', '&.Mui-focused': { color: '#00C896' } } }} />
              </Box>

              {/* Map marker label (4 chars max) — shown on the POS live map */}
              <TextField fullWidth
                label="Map Marker Label (max 4 chars)"
                value={formData.map_code}
                onChange={(e) => {
                  const v = e.target.value.toUpperCase().slice(0, 4);
                  setFormData({ ...formData, map_code: v });
                }}
                inputProps={{ maxLength: 4, style: { textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 700 } }}
                placeholder="e.g. F01"
                variant="outlined" InputLabelProps={{ shrink: true }}
                helperText="Shown on the driver's pin on the POS live map. Keep it short and unique (e.g. F01, H02, AR1)."
                FormHelperTextProps={{ sx: { color: '#6b7280', ml: 0 } }}
                sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#1e2139', color: '#e0e0e0', '& fieldset': { borderColor: '#3a3d52' }, '&:hover fieldset': { borderColor: '#00C896' } },
                  '& .MuiInputLabel-root': { color: '#9ca3af', '&.Mui-focused': { color: '#00C896' } } }} />

              {/* Password */}
              <TextField fullWidth
                label={editingDriver ? t('drivers.newPasswordOptional') : t('common.password')}
                type={showPassword ? 'text' : 'password'} value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                variant="outlined" InputLabelProps={{ shrink: true }}
                helperText={editingDriver ? t('drivers.passwordChangeHelper') : t('drivers.passwordMinChars')}
                FormHelperTextProps={{ sx: { color: '#6b7280', ml: 0 } }}
                sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#1e2139', color: '#e0e0e0', '& fieldset': { borderColor: '#3a3d52' }, '&:hover fieldset': { borderColor: '#00C896' } },
                  '& .MuiInputLabel-root': { color: '#9ca3af', '&.Mui-focused': { color: '#00C896' } } }}
                InputProps={{ endAdornment: (
                  <InputAdornment position="end">
                    <MUIIconButton onClick={() => setShowPassword(!showPassword)} edge="end" sx={{ color: '#6b7280' }}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </MUIIconButton>
                  </InputAdornment>
                ) }} />

              {/* Confirm password (only when password entered or new driver) */}
              {(formData.password || !editingDriver) && (
                <TextField fullWidth label={t('drivers.confirmPassword')}
                  type={showPassword ? 'text' : 'password'} value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  variant="outlined" InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#1e2139', color: '#e0e0e0', '& fieldset': { borderColor: '#3a3d52' }, '&:hover fieldset': { borderColor: '#00C896' } },
                    '& .MuiInputLabel-root': { color: '#9ca3af', '&.Mui-focused': { color: '#00C896' } } }} />
              )}

              {/* Phone & Vehicle row */}
              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                <TextField fullWidth label={t('common.phone')} value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  variant="outlined" InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#1e2139', color: '#e0e0e0', '& fieldset': { borderColor: '#3a3d52' }, '&:hover fieldset': { borderColor: '#00C896' } },
                    '& .MuiInputLabel-root': { color: '#9ca3af', '&.Mui-focused': { color: '#00C896' } } }} />
                <TextField fullWidth select label={t('drivers.vehicleType')} value={formData.vehicle_type}
                  onChange={(e) => setFormData({ ...formData, vehicle_type: e.target.value as 'bike' | 'scooter' | 'car' })}
                  SelectProps={{ native: true }}
                  variant="outlined" InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#1e2139', color: '#e0e0e0', '& fieldset': { borderColor: '#3a3d52' }, '&:hover fieldset': { borderColor: '#00C896' } },
                    '& .MuiInputLabel-root': { color: '#9ca3af', '&.Mui-focused': { color: '#00C896' } },
                    '& option': { backgroundColor: '#1e2139', color: '#e0e0e0' } }}>
                  <option value="bike">{t('drivers.bike')}</option>
                  <option value="scooter">{t('drivers.scooter')}</option>
                  <option value="car">{t('drivers.car')}</option>
                </TextField>
              </Box>

              {/* License Plate */}
              <TextField fullWidth label={t('drivers.licensePlate')} value={formData.license_plate}
                onChange={(e) => setFormData({ ...formData, license_plate: e.target.value })}
                variant="outlined" InputLabelProps={{ shrink: true }} placeholder="e.g. K-AZ 5011"
                sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#1e2139', color: '#e0e0e0', '& fieldset': { borderColor: '#3a3d52' }, '&:hover fieldset': { borderColor: '#00C896' } },
                  '& .MuiInputLabel-root': { color: '#9ca3af', '&.Mui-focused': { color: '#00C896' } } }} />

              {/* Joined date (read-only when editing) */}
              {editingDriver && (
                <TextField fullWidth label={t('drivers.joinedDate')}
                  value={(() => {
                    const v = editingDriver.joined_date;
                    if (!v) return 'N/A';
                    try {
                      const d = new Date(v);
                      if (!isNaN(d.getTime())) return d.toLocaleDateString('de-DE');
                    } catch {}
                    return String(v);
                  })()}
                  disabled variant="outlined" InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#1a1d2e', color: '#6b7280', '& fieldset': { borderColor: '#2a2d3e' } },
                    '& .MuiInputLabel-root': { color: '#6b7280' } }} />
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: { xs: 2, md: 3 }, py: 2, borderTop: '1px solid #3a3d52', gap: 1 }}>
            <Button onClick={handleCloseDialog} sx={{ color: '#9ca3af', textTransform: 'none', fontWeight: 500 }}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSaveDriver} variant="contained"
              sx={{ backgroundColor: '#00C896', color: '#1a1d2e', textTransform: 'none', fontWeight: 600, px: 3,
                borderRadius: 2, '&:hover': { backgroundColor: '#7bc912' } }}>
              {editingDriver ? t('drivers.updateDriver') : t('drivers.addDriver')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Driver Report Dialog */}
        <Dialog open={!!reportDriver} onClose={() => { setReportDriver(null); setReportData(null); }}
          maxWidth="md" fullWidth
          PaperProps={{ sx: { borderRadius: 3, backgroundColor: '#1a1d2e', border: '1px solid #2a2d42' } }}>
          {reportDriver && (
            <>
              <DialogTitle sx={{ borderBottom: '1px solid #2a2d42', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: '#00C896', color: '#1a1d2e', fontWeight: 700 }}>
                    {reportDriver.name.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography sx={{ color: '#e0e0e0', fontWeight: 700, fontSize: '1.1rem' }}>
                      {reportDriver.name} — Driver Report
                    </Typography>
                    <Typography sx={{ color: '#9ca3af', fontSize: '0.8rem' }}>
                      {reportDriver.email} · {reportDriver.phone}
                    </Typography>
                  </Box>
                </Box>
                <MUIIconButton onClick={() => { setReportDriver(null); setReportData(null); }} sx={{ color: '#9ca3af' }}>
                  <Close />
                </MUIIconButton>
              </DialogTitle>
              <DialogContent sx={{ pt: 3 }}>
                {reportLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress sx={{ color: '#00C896' }} />
                  </Box>
                ) : reportData ? (
                  <>
                    {/* Driver Info Cards */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid item xs={6} md={3}>
                        <Card sx={{ bgcolor: 'rgba(76,175,80,0.08)', border: '1px solid rgba(76,175,80,0.2)', boxShadow: 'none' }}>
                          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Typography sx={{ color: '#9ca3af', fontSize: '0.7rem', fontWeight: 600, mb: 0.5 }}>TOTAL DELIVERIES</Typography>
                            <Typography sx={{ color: '#4CAF50', fontSize: '1.5rem', fontWeight: 700 }}>{reportData.totalDeliveries}</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Card sx={{ bgcolor: 'rgba(0, 200, 150,0.08)', border: '1px solid rgba(0, 200, 150,0.2)', boxShadow: 'none' }}>
                          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Typography sx={{ color: '#9ca3af', fontSize: '0.7rem', fontWeight: 600, mb: 0.5 }}>TOTAL SALES HANDLED</Typography>
                            <Typography sx={{ color: '#00C896', fontSize: '1.5rem', fontWeight: 700 }}>€{reportData.totalEarnings.toFixed(2)}</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Card sx={{ bgcolor: 'rgba(33,150,243,0.08)', border: '1px solid rgba(33,150,243,0.2)', boxShadow: 'none' }}>
                          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Typography sx={{ color: '#9ca3af', fontSize: '0.7rem', fontWeight: 600, mb: 0.5 }}>AVG DELIVERY TIME</Typography>
                            <Typography sx={{ color: '#64B5F6', fontSize: '1.5rem', fontWeight: 700 }}>{reportData.avgDeliveryTime} min</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Card sx={{ bgcolor: 'rgba(255,152,0,0.08)', border: '1px solid rgba(255,152,0,0.2)', boxShadow: 'none' }}>
                          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Typography sx={{ color: '#9ca3af', fontSize: '0.7rem', fontWeight: 600, mb: 0.5 }}>JOINED</Typography>
                            <Typography sx={{ color: '#FF9800', fontSize: '0.95rem', fontWeight: 700 }}>
                              {(() => {
                                const v = reportDriver.joined_date;
                                if (!v) return 'N/A';
                                try { const d = new Date(v); if (!isNaN(d.getTime())) return d.toLocaleDateString('de-DE'); } catch {}
                                return 'N/A';
                              })()}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>

                    {/* Period Stats */}
                    <Typography sx={{ color: '#e0e0e0', fontWeight: 700, mb: 1, fontSize: '0.9rem' }}>
                      <TrendingUp sx={{ fontSize: 18, verticalAlign: 'middle', mr: 0.5, color: '#00C896' }} />
                      Delivery Activity
                    </Typography>
                    <Grid container spacing={1.5} sx={{ mb: 3 }}>
                      <Grid item xs={4}>
                        <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid #2a2d42', textAlign: 'center' }}>
                          <Typography sx={{ color: '#9ca3af', fontSize: '0.7rem' }}>Today</Typography>
                          <Typography sx={{ color: '#00C896', fontSize: '1.3rem', fontWeight: 700 }}>{reportData.todayDeliveries}</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid #2a2d42', textAlign: 'center' }}>
                          <Typography sx={{ color: '#9ca3af', fontSize: '0.7rem' }}>Last 7 days</Typography>
                          <Typography sx={{ color: '#64B5F6', fontSize: '1.3rem', fontWeight: 700 }}>{reportData.weekDeliveries}</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid #2a2d42', textAlign: 'center' }}>
                          <Typography sx={{ color: '#9ca3af', fontSize: '0.7rem' }}>Last 30 days</Typography>
                          <Typography sx={{ color: '#FF9800', fontSize: '1.3rem', fontWeight: 700 }}>{reportData.monthDeliveries}</Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    {/* Active Sessions History */}
                    {reportData.activeHistory.length > 0 && (
                      <>
                        <Typography sx={{ color: '#e0e0e0', fontWeight: 700, mb: 1, fontSize: '0.9rem' }}>
                          <AccessTime sx={{ fontSize: 18, verticalAlign: 'middle', mr: 0.5, color: '#00C896' }} />
                          Work Hours by Day (last 30 days)
                        </Typography>
                        <TableContainer sx={{ mb: 3, border: '1px solid #2a2d42', borderRadius: 1.5, maxHeight: 220 }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ bgcolor: '#1e2130', color: '#9ca3af', fontSize: '0.75rem', borderBottom: '1px solid #2a2d42' }}>Date</TableCell>
                                <TableCell sx={{ bgcolor: '#1e2130', color: '#9ca3af', fontSize: '0.75rem', borderBottom: '1px solid #2a2d42' }} align="right">Deliveries</TableCell>
                                <TableCell sx={{ bgcolor: '#1e2130', color: '#9ca3af', fontSize: '0.75rem', borderBottom: '1px solid #2a2d42' }} align="right">Work Hours</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {reportData.activeHistory.map((h, i) => (
                                <TableRow key={i}>
                                  <TableCell sx={{ color: '#e0e0e0', fontSize: '0.8rem', borderBottom: '1px solid #2a2d42' }}>
                                    {new Date(h.date).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' })}
                                  </TableCell>
                                  <TableCell sx={{ color: '#00C896', fontSize: '0.8rem', fontWeight: 600, borderBottom: '1px solid #2a2d42' }} align="right">{reportData.deliveries.filter((d: any) => {
                                    const day = d.created_at.toISOString().split('T')[0];
                                    return day === h.date && (d.status === 'delivered' || d.status === 'completed');
                                  }).length}</TableCell>
                                  <TableCell sx={{ color: '#64B5F6', fontSize: '0.8rem', fontWeight: 600, borderBottom: '1px solid #2a2d42' }} align="right">{h.hours.toFixed(1)}h</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </>
                    )}

                    {/* Delivery History */}
                    <Typography sx={{ color: '#e0e0e0', fontWeight: 700, mb: 1, fontSize: '0.9rem' }}>
                      <LocalShipping sx={{ fontSize: 18, verticalAlign: 'middle', mr: 0.5, color: '#00C896' }} />
                      Recent Deliveries ({reportData.deliveries.length})
                    </Typography>
                    <TableContainer sx={{ border: '1px solid #2a2d42', borderRadius: 1.5, maxHeight: 300 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ bgcolor: '#1e2130', color: '#9ca3af', fontSize: '0.72rem', borderBottom: '1px solid #2a2d42' }}>Order</TableCell>
                            <TableCell sx={{ bgcolor: '#1e2130', color: '#9ca3af', fontSize: '0.72rem', borderBottom: '1px solid #2a2d42' }}>Customer</TableCell>
                            <TableCell sx={{ bgcolor: '#1e2130', color: '#9ca3af', fontSize: '0.72rem', borderBottom: '1px solid #2a2d42' }}>Date / Time</TableCell>
                            <TableCell sx={{ bgcolor: '#1e2130', color: '#9ca3af', fontSize: '0.72rem', borderBottom: '1px solid #2a2d42' }} align="right">Total</TableCell>
                            <TableCell sx={{ bgcolor: '#1e2130', color: '#9ca3af', fontSize: '0.72rem', borderBottom: '1px solid #2a2d42' }}>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {reportData.deliveries.length === 0 ? (
                            <TableRow><TableCell colSpan={5} sx={{ color: '#6b7280', fontSize: '0.8rem', textAlign: 'center', py: 3 }}>No deliveries yet.</TableCell></TableRow>
                          ) : reportData.deliveries.map((d: any) => {
                            const isDone = d.status === 'delivered' || d.status === 'completed';
                            const isCancelled = d.status === 'cancelled';
                            return (
                              <TableRow key={d.id}>
                                <TableCell sx={{ color: '#00C896', fontWeight: 600, fontSize: '0.78rem', borderBottom: '1px solid #2a2d42' }}>#{d.order_number}</TableCell>
                                <TableCell sx={{ color: '#e0e0e0', fontSize: '0.78rem', borderBottom: '1px solid #2a2d42' }}>{d.customer_name || '—'}</TableCell>
                                <TableCell sx={{ color: '#9ca3af', fontSize: '0.72rem', borderBottom: '1px solid #2a2d42' }}>
                                  {d.created_at.toLocaleDateString('de-DE')} {d.created_at.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                                </TableCell>
                                <TableCell sx={{ color: '#00C896', fontWeight: 600, fontSize: '0.78rem', borderBottom: '1px solid #2a2d42' }} align="right">€{parseFloat(d.total).toFixed(2)}</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #2a2d42' }}>
                                  <Chip
                                    icon={isDone ? <CheckCircle sx={{ fontSize: 14 }} /> : isCancelled ? <Cancel sx={{ fontSize: 14 }} /> : <AccessTime sx={{ fontSize: 14 }} />}
                                    label={d.status}
                                    size="small"
                                    sx={{
                                      bgcolor: isDone ? 'rgba(76,175,80,0.15)' : isCancelled ? 'rgba(244,67,54,0.15)' : 'rgba(255,152,0,0.15)',
                                      color: isDone ? '#4CAF50' : isCancelled ? '#ef5350' : '#FF9800',
                                      fontSize: '0.65rem', fontWeight: 600, height: 22,
                                    }}
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                ) : (
                  <Alert severity="info" sx={{ bgcolor: 'rgba(33,150,243,0.1)', color: '#64B5F6' }}>
                    No report data available.
                  </Alert>
                )}
              </DialogContent>
              <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #2a2d42' }}>
                <Button onClick={() => { setReportDriver(null); setReportData(null); }}
                  sx={{ color: '#9ca3af', textTransform: 'none' }}>Close</Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Box>
    </AdminLayout>
  );
};

export default DriversPage;
